// Lab Archive — read-only browser + semantic search over archive_articles.
// Phase 0 scope: lets operators see what the AI-enrichment pipeline produced
// before we build the composer on top (Phase 2).

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { fmtDateTime } from '../lib/format'
import {
  STATUS_CLASS, publishError, statusLabel, usePublishArticle,
} from './usePublishArticle'
import { ScheduleControl } from './ScheduleControl'
import { MemberPageLink } from './MemberPageLink'
import { ArchiveSyncBar } from './ArchiveSyncBar'

const PUB_NAMES: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  diecast: 'Die Cast X',
}

interface ArticleCategoryOption {
  label: string
  article_count: number
  digital_edition_id: number | null
}

const STORY_TYPES = [
  'firsthand_account',
  'technical_analysis',
  'historical_deep_dive',
  'iconic_aircraft_vehicle',
  'combat_story',
  'review',
  'how_to',
  'product_announcement',
  'interview',
  'profile',
  'editorial',
  'news',
  'other',
]

type ArchiveArticle = {
  id: number
  publication_slug: string
  site_type: string
  title: string
  slug: string | null
  wp_url: string
  wp_post_id: number
  post_date: string | null
  featured_image_url: string | null
  story_type: string | null
  era: string | null
  ai_tags: string[] | null
  short_teaser: string | null
  long_teaser: string | null
  ai_confidence: number | null
  ai_model: string | null
  editor_priority: number
  engagement_score: number
  enriched: boolean
  embedded: boolean
  // WordPress' article_category: the issue this ran in, e.g.
  // "February 2020 Issue". Null on web-only posts.
  wp_article_category: string | null
  digital_edition_id: number | null
  // 'active' = live in the membership, 'excluded' = unpublished by an
  // editor, 'stale' = the WordPress post is gone.
  status: string
  unpublished_at: string | null
  unpublished_by: string | null
  // 'backcat_retired' / 'out_of_date' mark a retired row.
  unpublish_reason?: string | null
  // Scheduled publish: when this unpublished row goes live (UTC ISO), and
  // why the last schedule could not fire.
  publish_at?: string | null
  publish_schedule_error?: string | null
}

type ArchiveList = {
  total: number
  items: ArchiveArticle[]
  offset: number
  limit: number
}

type ArchiveStats = {
  total: number
  enriched: number
  embedded: number
  by_publication: Record<string, { total: number; enriched: number; embedded: number }>
}

type SearchHit = {
  id: number
  title: string
  short_teaser: string | null
  wp_url: string
  featured_image_url: string | null
  story_type: string | null
  wp_article_category: string | null
  digital_edition_id: number | null
  era: string | null
  ai_tags: string[] | null
  publication_slug: string
  similarity: number
  post_date: string | null
  status: string
  slug: string | null
}

type SearchResponse = { query: string; hits: SearchHit[] }


export type EnrichmentFilter =
  | 'any'
  | 'enriched'
  /** Un-enriched AND with text to work from — the real queue. */
  | 'needs_enrichment'
  /** Un-enriched with nothing to enrich: video embeds, empty posts. */
  | 'no_text'

/** The articles one enrichment run touched, pinned for review. */
export type IdsView = { ids: number[]; label: string }

type Mode = 'browse' | 'search' | 'ids'

export function LabArchivePage() {
  const [mode, setMode] = useState<Mode>('browse')
  const [pubFilter, setPubFilter] = useState<string>('')
  const [storyTypeFilter, setStoryTypeFilter] = useState<string>('')
  const [issueFilter, setIssueFilter] = useState<string>('')
  // Default 'any': an article synced but not yet enriched is still an
  // article, and hiding it by default made a successful sync look like a
  // no-op. 'unenriched' is the enrichment work queue.
  const [enrichment, setEnrichment] = useState<EnrichmentFilter>('any')
  // Non-null when showing the exact articles a run touched.
  const [idsView, setIdsView] = useState<IdsView | null>(null)
  // '' = the states an editor manages (published + unpublished). 'stale'
  // rows are sync wreckage and stay out of the way unless asked for.
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [query, setQuery] = useState<string>('')
  const [submittedQuery, setSubmittedQuery] = useState<string>('')

  // Issue labels come from the API, not a constant: there are 250+ of
  // them across the four titles and the list grows with every issue.
  const issueOptions = useQuery({
    queryKey: ['lab', 'archive', 'article-categories', pubFilter],
    queryFn: () => {
      const p = new URLSearchParams()
      if (pubFilter) p.set('publication_slug', pubFilter)
      return api.get<ArticleCategoryOption[]>(
        `/api/lab/archive/article-categories?${p.toString()}`,
      )
    },
    staleTime: 60_000,
  })

  const stats = useQuery({
    queryKey: ['lab', 'archive', 'stats'],
    queryFn: () => api.get<ArchiveStats>('/api/lab/archive/stats'),
    staleTime: 30_000,
  })

  const list = useQuery({
    queryKey: [
      'lab', 'archive', 'list',
      pubFilter, storyTypeFilter, enrichment, statusFilter, issueFilter,
    ],
    queryFn: () => {
      const p = new URLSearchParams({ limit: '100' })
      if (pubFilter) p.set('publication_slug', pubFilter)
      if (storyTypeFilter) p.set('story_type', storyTypeFilter)
      if (issueFilter) {
        // "<edition id>|<label>". Hand-linked articles have no label; the
        // issue id brings them in. Two titles can share a label, so the
        // id also tells their December issues apart.
        const [edition, ...label] = issueFilter.split('|')
        p.set('article_category', label.join('|'))
        if (edition) p.set('digital_edition_id', edition)
      }
      if (enrichment !== 'any') p.set('enrichment', enrichment)
      if (statusFilter) p.set('status', statusFilter)
      return api.get<ArchiveList>(`/api/lab/archive?${p.toString()}`)
    },
    enabled: mode === 'browse',
  })

  const search = useQuery({
    queryKey: ['lab', 'archive', 'search', submittedQuery, pubFilter],
    queryFn: () =>
      api.post<SearchResponse>('/api/lab/archive/search', {
        query: submittedQuery,
        publication_slug: pubFilter || null,
        limit: 20,
      }),
    enabled: mode === 'search' && submittedQuery.length > 0,
  })


  // The exact articles a run touched. `by-ids` preserves caller order and
  // caps at 200, which covers a full batch of 50 several times over.
  const idsList = useQuery({
    queryKey: ['lab', 'archive', 'by-ids', idsView?.ids ?? []],
    queryFn: () =>
      api.get<ArchiveArticle[]>(
        `/api/lab/archive/by-ids?ids=${(idsView?.ids ?? []).join(',')}`,
      ),
    enabled: mode === 'ids' && !!idsView?.ids.length,
  })

  const showArticles = (ids: number[], label: string) => {
    if (!ids.length) return
    setIdsView({ ids, label })
    setMode('ids')
  }

  // "12 awaiting" -> the actual 12, as a normal filtered browse.
  const showFiltered = (slug: string, filter: EnrichmentFilter) => {
    setPubFilter(slug)
    setEnrichment(filter)
    setStatusFilter('')
    setIdsView(null)
    setMode('browse')
  }
  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setMode('search')
    setSubmittedQuery(query)
  }

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <Header
        stats={stats.data}
        onShowArticles={showArticles}
        onShowNeedsEnrichment={(slug) => showFiltered(slug, 'needs_enrichment')}
        onShowNoText={(slug) => showFiltered(slug, 'no_text')}
      />

      <form onSubmit={onSearchSubmit} className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[320px]">
          <label className="label block mb-1">Semantic Search</label>
          <input
            type="text"
            className="w-full bg-paper-deepest border border-paper-trace px-3 py-2 font-serif text-paper"
            placeholder={`e.g. "pilot survives against the odds"`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div>
          <label className="label block mb-1">Publication</label>
          <select
            value={pubFilter}
            onChange={(e) => setPubFilter(e.target.value)}
            className="bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper"
          >
            <option value="">All publications</option>
            {Object.entries(PUB_NAMES).map(([slug, name]) => (
              <option key={slug} value={slug}>{name}</option>
            ))}
          </select>
        </div>
        {mode === 'browse' && (
          <>
            <div>
              <label className="label block mb-1">Story Type</label>
              <select
                value={storyTypeFilter}
                onChange={(e) => setStoryTypeFilter(e.target.value)}
                className="bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper"
              >
                <option value="">All types</option>
                {STORY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="label block mb-1">Issue</label>
              <select
                value={issueFilter}
                onChange={(e) => setIssueFilter(e.target.value)}
                className="bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper"
              >
                <option value="">All issues</option>
                {(issueOptions.data ?? []).map((o) => (
                  <option
                    key={`${o.digital_edition_id ?? ''}|${o.label}`}
                    value={`${o.digital_edition_id ?? ''}|${o.label}`}
                  >
                    {o.label} ({o.article_count})
                    {o.digital_edition_id === null ? ' · no edition' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label block mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper"
              >
                <option value="">Published + unpublished</option>
                <option value="active">Published only</option>
                <option value="excluded">Unpublished only</option>
                <option value="scheduled">Scheduled to publish</option>
                <option value="stale">Gone from WordPress</option>
                <option value="retired">Retired (both)</option>
                <option value="retired_backcat">Retired: back catalogue</option>
                <option value="retired_out_of_date">Retired: out of date</option>
                <option value="all">Everything</option>
              </select>
            </div>
            <div>
              <label className="label block mb-1">Enrichment</label>
              <select
                value={enrichment}
                onChange={(e) => setEnrichment(e.target.value as EnrichmentFilter)}
                className="bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper"
              >
                <option value="any">All articles</option>
                <option value="enriched">Enriched</option>
                <option value="needs_enrichment">Needs enrichment</option>
                <option value="no_text">No text to enrich</option>
              </select>
            </div>
          </>
        )}
        <div className="flex gap-2">
          <button
            type="submit"
            className="px-4 py-2 bg-amber-glow text-paper-deepest font-mono text-[12px] tracking-label hover:opacity-90"
          >
            SEARCH
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('browse')
              setSubmittedQuery('')
              setQuery('')
            }}
            className="px-4 py-2 border border-paper-trace text-paper font-mono text-[12px] tracking-label hover:bg-paper-trace/20"
          >
            BROWSE
          </button>
        </div>
      </form>

      {mode === 'ids' && idsView ? (
        <RunResults
          label={idsView.label}
          rows={idsList.data}
          loading={idsList.isLoading}
          onClear={() => { setIdsView(null); setMode('browse') }}
        />
      ) : mode === 'search' && submittedQuery ? (
        <SearchResults data={search.data} loading={search.isLoading} query={submittedQuery} />
      ) : (
        <BrowseResults data={list.data} loading={list.isLoading} />
      )}
    </div>
  )
}

function Header({
  stats, onShowArticles, onShowNeedsEnrichment, onShowNoText,
}: {
  stats: ArchiveStats | undefined
  onShowArticles: (ids: number[], label: string) => void
  onShowNeedsEnrichment: (slug: string) => void
  onShowNoText: (slug: string) => void
}) {
  return (
    <div className="border border-paper-trace bg-paper-deep p-4">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="label">LAB · 01</div>
          <h1 className="font-serif text-2xl text-paper mt-1">Archive</h1>
          <p className="text-paper-trace text-[13px] mt-1 font-serif">
            AI-enriched content from the publications' WordPress archives.
            Browse or search semantically; results update as the nightly
            pipeline enriches new articles.
          </p>
        </div>
        {stats && (
          <div className="grid grid-cols-3 gap-6 font-mono">
            <Stat label="Articles" value={stats.total.toLocaleString()} />
            <Stat label="Enriched" value={stats.enriched.toLocaleString()} sub={pct(stats.enriched, stats.total)} />
            <Stat label="Embedded" value={stats.embedded.toLocaleString()} sub={pct(stats.embedded, stats.total)} />
          </div>
        )}
      </div>
      {stats && (
        <div className="mt-4 grid grid-cols-4 gap-3 border-t border-paper-trace pt-3">
          {Object.entries(PUB_NAMES).map(([slug, name]) => {
            const s = stats.by_publication[slug] ?? { total: 0, enriched: 0, embedded: 0 }
            return (
              <div key={slug} className="font-mono text-[11px]">
                <div className="label">{name}</div>
                <div className="text-paper mt-0.5">
                  {s.enriched}<span className="text-paper-trace">/{s.total}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}
      <ArchiveSyncBar
        onShowArticles={onShowArticles}
        onShowNeedsEnrichment={onShowNeedsEnrichment}
        onShowNoText={onShowNoText}
      />
    </div>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="text-right">
      <div className="label">{label}</div>
      <div className="text-paper text-2xl mt-0.5">{value}</div>
      {sub && <div className="text-paper-trace text-[11px]">{sub}</div>}
    </div>
  )
}

function pct(n: number, d: number): string {
  if (!d) return '—'
  return `${((n / d) * 100).toFixed(1)}%`
}

function SearchResults({ data, loading, query }: { data: SearchResponse | undefined; loading: boolean; query: string }) {
  if (loading) return <Loading />
  if (!data || data.hits.length === 0) return <Empty text={`No matches for "${query}"`} />
  return (
    <div className="space-y-3">
      <div className="label">
        {data.hits.length} results for "{query}" — ranked by semantic similarity
      </div>
      {data.hits.map((hit) => (
        <ArticleCard
          key={hit.id}
          id={hit.id}
          title={hit.title}
          pub={hit.publication_slug}
          storyType={hit.story_type}
          issueLabel={hit.wp_article_category}
          postDate={hit.post_date}
          editionId={hit.digital_edition_id}
          era={hit.era}
          tags={hit.ai_tags}
          teaser={hit.short_teaser}
          image={hit.featured_image_url}
          status={hit.status}
          slug={hit.slug}
          badge={
            <span className="font-mono text-[10px] text-amber-glow">
              {hit.similarity.toFixed(3)}
            </span>
          }
        />
      ))}
    </div>
  )
}


/** The exact articles one enrichment run touched.
 *
 *  Pinned rather than filtered: after a run the operator wants "these
 *  twelve", not "everything matching what those twelve have in common". */
function RunResults({
  label, rows, loading, onClear,
}: {
  label: string
  rows: ArchiveArticle[] | undefined
  loading: boolean
  onClear: () => void
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4 border border-amber-glow/40 bg-amber-glow/5 px-3 py-2">
        <div className="label text-amber-glow">{label}</div>
        <button
          type="button"
          onClick={onClear}
          className="px-3 py-1 border border-paper-trace text-paper font-mono text-[11px] tracking-label hover:bg-paper-trace/20"
        >
          BACK TO BROWSE
        </button>
      </div>
      {loading ? <Loading /> : !rows || rows.length === 0 ? (
        <Empty text="Those articles are no longer in the archive" />
      ) : (
        rows.map((a) => (
          <ArticleCard
            key={a.id}
            id={a.id}
            title={a.title}
            pub={a.publication_slug}
            storyType={a.story_type}
            issueLabel={a.wp_article_category}
            postDate={a.post_date}
            editionId={a.digital_edition_id}
            era={a.era}
            tags={a.ai_tags}
            teaser={a.short_teaser}
            image={a.featured_image_url}
            status={a.status}
            unpublishReason={a.unpublish_reason}
            publishAt={a.publish_at}
            scheduleError={a.publish_schedule_error}
            slug={a.slug}
            badge={
              a.ai_confidence != null ? (
                <span className="font-mono text-[10px] text-radar-green">
                  {a.ai_confidence.toFixed(2)}
                </span>
              ) : (
                <span className="label text-paper-trace">UN-ENRICHED</span>
              )
            }
          />
        ))
      )}
    </div>
  )
}
function BrowseResults({ data, loading }: { data: ArchiveList | undefined; loading: boolean }) {
  if (loading) return <Loading />
  if (!data || data.items.length === 0) return <Empty text="No articles match these filters" />
  return (
    <div className="space-y-3">
      <div className="label">
        Showing {data.items.length} of {data.total.toLocaleString()} articles
      </div>
      {data.items.map((a) => (
        <ArticleCard
          key={a.id}
          id={a.id}
          title={a.title}
          pub={a.publication_slug}
          storyType={a.story_type}
          issueLabel={a.wp_article_category}
          postDate={a.post_date}
          editionId={a.digital_edition_id}
          era={a.era}
          tags={a.ai_tags}
          teaser={a.short_teaser}
          image={a.featured_image_url}
          status={a.status}
          unpublishReason={a.unpublish_reason}
          publishAt={a.publish_at}
          scheduleError={a.publish_schedule_error}
          slug={a.slug}
          badge={
            a.ai_confidence != null ? (
              <span
                className={`font-mono text-[10px] ${
                  a.ai_confidence >= 0.85 ? 'text-radar-green'
                  : a.ai_confidence >= 0.6 ? 'text-amber-glow'
                  : 'text-radar-violet'
                }`}
              >
                {a.ai_confidence.toFixed(2)}
              </span>
            ) : (
              <span className="label text-paper-trace">UN-ENRICHED</span>
            )
          }
        />
      ))}
    </div>
  )
}

// Publish/unpublish straight from the list. Unpublishing asks first —
// it is member-visible — while putting an article back applies at once.
// A 'stale' article offers no action: its WordPress post is gone, so the
// API would reject publishing it.
function CardPublishAction({ id, status }: { id: number; status: string }) {
  const [confirming, setConfirming] = useState(false)
  const flip = usePublishArticle(id)
  useEffect(() => { if (flip.isSuccess) setConfirming(false) }, [flip.isSuccess])

  if (status === 'stale') return null
  const live = status === 'active'
  const err = publishError(flip.error)

  if (confirming) {
    return (
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => flip.mutate(false)}
          disabled={flip.isPending}
          className="font-mono text-[10px] tracking-label uppercase border border-radar-red/60 text-radar-red px-2 py-0.5 hover:bg-radar-red/10 disabled:opacity-50"
        >
          {flip.isPending ? 'Working\u2026' : 'Confirm'}
        </button>
        <button
          onClick={() => setConfirming(false)}
          disabled={flip.isPending}
          className="font-mono text-[10px] tracking-label uppercase text-paper-trace hover:text-paper disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      {err && <span className="font-mono text-[10px] text-radar-red">{err}</span>}
      <button
        onClick={() => (live ? setConfirming(true) : flip.mutate(true))}
        disabled={flip.isPending}
        className={`font-mono text-[10px] tracking-label uppercase border px-2 py-0.5 disabled:opacity-50 ${
          live
            ? 'border-paper-trace text-paper-trace hover:text-paper hover:border-paper'
            : 'border-radar-green/60 text-radar-green hover:bg-radar-green/10'
        }`}
      >
        {flip.isPending ? 'Working\u2026' : live ? 'Unpublish' : 'Publish'}
      </button>
    </div>
  )
}

function ArticleCard(props: {
  id: number
  title: string
  pub: string
  storyType: string | null
  era: string | null
  tags: string[] | null
  teaser: string | null
  image: string | null
  badge: React.ReactNode
  status?: string
  unpublishReason?: string | null
  publishAt?: string | null
  scheduleError?: string | null
  slug?: string | null
  issueLabel?: string | null
  editionId?: number | null
  postDate?: string | null
}) {
  const viewerUrl = `/lab/archive/${props.id}`
  return (
    <div className="border border-paper-trace bg-paper-deep p-3 flex gap-4">
      {props.image ? (
        <Link to={viewerUrl} className="flex-shrink-0">
          <img
            src={props.image}
            alt=""
            className="w-28 h-20 object-cover border border-paper-trace"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden' }}
          />
        </Link>
      ) : (
        <Link to={viewerUrl} className="w-28 h-20 border border-paper-trace bg-paper-deepest flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <Link
            to={viewerUrl}
            className="font-serif text-paper text-[16px] leading-tight hover:text-amber-glow"
          >
            {props.title}
          </Link>
          <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
            {props.badge}
            {props.status && <CardPublishAction id={props.id} status={props.status} />}
            {props.status === 'excluded' && (
              <ScheduleControl
                id={props.id}
                publishAt={props.publishAt}
                lastError={props.scheduleError}
                compact
              />
            )}
          </div>
        </div>
        <div className="mt-1 flex flex-wrap gap-2 items-center">
          {props.status && (
            <span
              className={`font-mono text-[9px] tracking-label uppercase border px-1.5 py-0.5 ${
                STATUS_CLASS[props.status] ?? 'border-paper-trace text-paper-trace'
              }`}
            >
              {statusLabel(props.status, props.unpublishReason)}
            </span>
          )}
          <MemberPageLink
            publicationSlug={props.pub}
            slug={props.slug}
            status={props.status}
          />
          <span className="label text-paper-trace">{PUB_NAMES[props.pub] ?? props.pub}</span>
          {props.postDate && (
            <span
              className="font-mono text-[10px] tracking-label text-paper-dim tabular-nums"
              title="Published (WordPress post date)"
            >
              {fmtDateTime(props.postDate)}
            </span>
          )}
          {props.storyType && (
            <span className="font-mono text-[10px] tracking-label text-radar-cyan uppercase">
              {props.storyType}
            </span>
          )}
          {props.issueLabel && (
            props.editionId ? (
              <Link
                to={`/lab/issues/${props.editionId}`}
                className="font-mono text-[10px] tracking-label text-amber-glow uppercase hover:underline"
                title="Open the digital edition this ran in"
              >
                ◆ {props.issueLabel}
              </Link>
            ) : (
              <span
                className="font-mono text-[10px] tracking-label text-paper-trace uppercase"
                title="No digital edition matches this issue label"
              >
                ◆ {props.issueLabel}
              </span>
            )
          )}
          {props.era && (
            <span className="font-mono text-[10px] tracking-label text-paper-trace">
              · {props.era}
            </span>
          )}
        </div>
        {props.teaser && (
          <div className="mt-2 font-serif text-[13px] text-paper-trace leading-snug">
            {props.teaser}
          </div>
        )}
        {props.tags && props.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {props.tags.map((t) => (
              <span
                key={t}
                className="font-mono text-[10px] text-paper-trace border border-paper-trace px-1.5 py-0.5"
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function Loading() {
  return <div className="label text-paper-trace animate-pulse py-8 text-center">LOADING</div>
}

function Empty({ text }: { text: string }) {
  return <div className="label text-paper-trace py-8 text-center">{text}</div>
}

